import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import appDisplay from './app-display'
import globals from './globals'
import config from './config'
import i18n from './i18n'
import ConfirmPrompt from './confirm-prompt'
import scoreboardUrl from 'scoreboard/components/scoreboard-url'
import { XHR_HEADERS_AVAILABLE } from 'scoreboard/components/permissions'

var MINUTE_IN_MILLIS = 60*1000
/**
 * local storage key to track the last time there was mouse movement
 */
const LAST_USER_INTERACTION= 'lastIdleReset'
/**
 * local storage key to track the date of which the last XHR was performed
 */
const LAST_XHR= 'lastXhr'

/**
 *  Keeps track of a users interaction with the application and
 *  displays a logout message when there are 10 minutes remaining
 *  in their session.
 *
 *  When the session timer expires the user is logged out.
 */
var sessionTimer = {

	warningTimeout: undefined,
	stayLoggedInPrompt: undefined,
	promptShownAt: undefined,

	// after this many minutes user is warned they will be logged out
	minutesBeforePrompt: undefined,
	// they have this many minutes to click "stay logged in"
	minutesAfterPrompt: undefined,

	/**
	 * Starts the session timer and listens for click events which translate
	 * 99% of the time into server trips which extends the session
	 */
	start: function () {

		this.minutesAfterPrompt = 5
		this.minutesBeforePrompt =
			config.sessionTimeoutInterval - this.minutesAfterPrompt

		this.resetWarningTimeout()

		// Emitted when an XHR response is successful
		Backbone.on(XHR_HEADERS_AVAILABLE, () => {
			globals.storeLocal(LAST_XHR, Date.now())
		})

		const doc = $(document)
		// Don't fully rely on client timers because the OS can sleep, mobile
		// app can lose focus etc.  When the user comes back to the app
		// immediately reconcile the current time with the last click event
		// (Fixes #12362)
		const sessionDuration = this.minutesBeforePrompt + this.minutesAfterPrompt
		doc.mousemove(e => {
			const minsElapsed = this.minutesElapsed(LAST_USER_INTERACTION)
			if (minsElapsed > sessionDuration) {
				this.sendToLogin()
			} else if (!this.promptDisplayed()) {
				if (minsElapsed > this.minutesBeforePrompt) {
					this.displayStayLoggedInPrompt()
				} else {
					this.resetWarningTimeout()
					// User could be mousing around without any server interactions
					// So we must keep their session alive for them
					const minsSinceXhr = this.minutesElapsed(LAST_XHR)
					if (minsSinceXhr > this.minutesBeforePrompt && !this.keepAliveXhr) {
						this.keepAliveXhr = $.get('api/keepalive')
						// Don't to more than one at a time
						this.keepAliveXhr.then(() => {
							delete this.keepAliveXhr
						})
					}
				}
			}
		})
	},

	promptDisplayed: function() {
		return this.stayLoggedInPrompt !== undefined
	},

	timeRemainingMessage: function(times, now)  {
		const {minutes, seconds} = times || this.timeRemaining(now)
		const totalSeconds = minutes * 60 + seconds
		const showMinutes = totalSeconds > 60
		const key =`common.logOff.auto.warning.${showMinutes ? 'minutes' : 'seconds'}`
		return i18n.get(key, showMinutes ? (minutes+1) : totalSeconds)
	},

	minutesElapsed: function(key) {
		return (Date.now() - globals.readLocal(key)) / MINUTE_IN_MILLIS
	},

	timeRemaining: function(now) {
		const millisElapsed = _.defaultTo(now, Date.now()) - this.promptShownAt
		const millisRemaining = (this.minutesAfterPrompt * MINUTE_IN_MILLIS) - millisElapsed
		const minutes = Math.floor(millisRemaining / MINUTE_IN_MILLIS)
		const seconds = Math.floor((millisRemaining / 1000) % 60)
		return {minutes, seconds}
	},

	resetWarningTimeout: function(opts) {
		// We must not change the LAST_USER_INTERACTION due to mouse movement in this window
		// until the dialog is closed
		if (_.get(opts, 'force') || !this.promptDisplayed()) {
			clearTimeout(this.warningTimeout)
			globals.storeLocal(LAST_USER_INTERACTION, _.get(opts, 'lastReset', Date.now()))
			var delay = _.get(opts,
				'timeoutDuration', this.minutesBeforePrompt * MINUTE_IN_MILLIS)
			this.warningTimeout = setTimeout(this.checkIdleSession.bind(this), delay)
		}
	},

	checkIdleSession: function() {
		// the threshold of idleness has elapsed in this window, but check all other windows too
		if (!this.otherSessionActivity()) {
			this.displayStayLoggedInPrompt()
			this.countdownTimer()
		}
	},

	displayStayLoggedInPrompt: function() {
		this.promptShownAt = Date.now()
		const timer = this
		this.stayLoggedInPrompt = new ConfirmPrompt({
			titleKey: 'common.logOff.auto.dialogTitle',
			messageKey: this.timeRemainingMessage(),
			confirmLabel: 'common.logOff.auto.stayLoggedIn',
			confirmOnly: true,
			onConfirm: function () {
				timer.stayLoggedInPrompt = undefined
				timer.resetWarningTimeout({force: true})
				return $.get('api/keepalive')
			}
		})
		appDisplay.dialog(this.stayLoggedInPrompt)
	},

	/**
	 * Update the time remaining every second.
	 * When zero time remains user is automatically logged out.
	 * Timestamps are used because Timers have been found to be inaccurate.
	 */
	countdownTimer: function() {
		// This is really a 1s interval, but timeout is used to prevent leaked intervals
		setTimeout(() => {
			if (!this.otherSessionActivity()) {
				const remaining = this.timeRemaining()
				if (remaining.minutes < 0) {
					this.sendToLogin()
				} else {
					const updatedMsg = this.timeRemainingMessage(remaining)
					this.stayLoggedInPrompt.$('.message').text(updatedMsg)
					this.countdownTimer()
				}
			}
		}, 1000)
	},

	sendToLogin: function() {
		// If a customer does not have a logout redirect setup
		// then use the current view upon the next login
		// (https://github.com/SpiderStrategies/Scoreboard/issues/12362#issuecomment-312319964)
		globals.storeLocal('timedOut', true)
		location.href = scoreboardUrl('logout?url=' + encodeURIComponent(Backbone.history.fragment))
	},

	/**
	 * If any other window has had activity reset the timer
	 * @returns {boolean} true if there has been activity
	 */
	otherSessionActivity: function() {
		var elapsed = this.minutesElapsed(LAST_USER_INTERACTION)
		var hasActivity = elapsed < this.minutesBeforePrompt
		if (hasActivity) {
			this.resetWarningTimeout({
				lastReset: globals.readLocal(LAST_USER_INTERACTION),
				timeoutDuration: this.minutesBeforePrompt - elapsed
			})
			// If the user is active in another window, just close the dialog
			_.result(this.stayLoggedInPrompt, 'submit')
			this.stayLoggedInPrompt = undefined
		}
		return hasActivity
	}
}

export default sessionTimer
sessionTimer.__filename__ = 'components/session-timer.js'
